import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { communityTopics } from "@/mocks/community";
import useDocumentTitle from "@/hooks/useDocumentTitle";
import Breadcrumb from "@/components/base/Breadcrumb";
import EmptyState from "@/components/base/EmptyState";

const tabs = [
  { key: "latest", label: "最新话题", icon: "ri-time-line" },
  { key: "hot", label: "热门讨论", icon: "ri-fire-line" },
  { key: "recommend", label: "动漫推荐讨论区", icon: "ri-thumb-up-line" },
];

export default function CommunityPage() {
  useDocumentTitle(
    "动漫社区 - 漫蛙2动漫资讯平台",
    "漫蛙2动漫社区，分享追番心得、参与新番讨论、安利冷门佳作，和同好一起聊动漫。"
  );
  const [tab, setTab] = useState("latest");
  const [keyword, setKeyword] = useState("");

  const list = useMemo(() => {
    let items = [...communityTopics];
    if (tab === "hot") items.sort((a, b) => b.replies - a.replies);
    if (tab === "recommend") items = items.filter((t) => t.category === "动漫推荐讨论区");
    const kw = keyword.trim();
    if (kw) items = items.filter((t) => t.title.includes(kw) || t.content.includes(kw));
    return items;
  }, [tab, keyword]);

  return (
    <div className="pb-4">
      <section className="border-b border-background-200 bg-background-100">
        <div className="page-wrap py-8 md:py-12">
          <Breadcrumb items={[{ label: "动漫社区" }]} />
          <h1 className="mt-4 text-2xl font-extrabold text-foreground-950 md:text-3xl">动漫社区</h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
            分享追番心得、参与新番讨论、安利冷门佳作。社区内容为演示数据，欢迎体验互动流程。
          </p>
        </div>
      </section>

      <div className="page-wrap mt-8">
        <div className="rounded-lg border border-primary-200 bg-primary-50 p-5">
          <div className="flex items-start gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary-500 text-background-50">
              <i className="ri-megaphone-line text-xl"></i>
            </span>
            <div>
              <h2 className="text-base font-bold text-foreground-950">精选社区活动 · 秋季新番安利季</h2>
              <p className="mt-1 text-sm leading-relaxed text-foreground-700">
                在「动漫推荐讨论区」发帖安利你本季最爱的作品，优质内容将在首页推荐位展示。
              </p>
            </div>
          </div>
        </div>

        <div className="mt-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="flex flex-wrap items-center gap-1 rounded-full bg-background-100 p-1">
            {tabs.map((item) => (
              <button
                key={item.key}
                type="button"
                onClick={() => setTab(item.key)}
                className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                  tab === item.key ? "bg-background-50 text-primary-700" : "text-foreground-600"
                }`}
              >
                <i className={item.icon}></i>
                {item.label}
              </button>
            ))}
          </div>

          <div className="flex h-10 items-center gap-2 rounded-md border border-background-200 bg-background-50 px-3 md:w-64">
            <i className="ri-search-line text-base text-foreground-400" aria-hidden="true"></i>
            <input
              type="search"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索社区话题"
              aria-label="搜索社区话题"
              className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
            />
          </div>
        </div>

        {list.length === 0 ? (
          <div className="mt-6">
            <EmptyState
              title="没有找到相关话题"
              description="换个关键词试试，或者到热门讨论里看看大家都在聊什么。"
              actionLabel="查看全部话题"
              actionTo="/community"
            />
          </div>
        ) : (
          <ul className="mt-6 flex flex-col gap-3">
            {list.map((topic) => (
              <li key={topic.id}>
                <Link
                  to={`/community/${topic.id}`}
                  className="group flex items-start gap-4 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300 md:p-5"
                >
                  <div className="h-11 w-11 shrink-0 overflow-hidden rounded-full bg-background-200">
                    <img
                      src={topic.avatar}
                      alt={`${topic.author} 头像`}
                      title={`${topic.author} - 漫蛙2社区`}
                      className="h-full w-full object-cover object-top"
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="rounded-full bg-secondary-100 px-2.5 py-0.5 text-xs font-medium text-secondary-800">
                        {topic.category}
                      </span>
                      <span className="text-xs text-foreground-400">{topic.createdAt}</span>
                    </div>
                    <h3 className="mt-2 line-clamp-2 text-sm font-semibold text-foreground-950 transition-colors group-hover:text-primary-700 md:text-base">
                      {topic.title}
                    </h3>
                    <p className="mt-1.5 line-clamp-1 hidden text-xs text-foreground-600 sm:block">{topic.content}</p>
                    <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-foreground-500">
                      <span className="flex items-center gap-1">
                        <i className="ri-user-3-line"></i>
                        {topic.author}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-chat-3-line"></i>
                        {topic.replies} 回复
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-eye-line"></i>
                        {topic.views}
                      </span>
                    </div>
                  </div>
                  <i className="ri-arrow-right-line mt-1 hidden shrink-0 text-foreground-300 transition-colors group-hover:text-primary-500 sm:block"></i>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}